import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Bot, CheckCircle2, CircleAlert, FileText, Image as ImageIcon, Loader2,
  Paperclip, RefreshCw, Send, UserRound, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "gimmicks-agent-demo-session";
const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

function newSessionId() {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `gimmicks-demo-${random}`;
}
function getInitialSession() {
  const current = window.sessionStorage.getItem(SESSION_KEY);
  if (current) return current;
  const created = newSessionId();
  window.sessionStorage.setItem(SESSION_KEY, created);
  return created;
}
function sizeLabel(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AgentDemoChat() {
  const [sessionId, setSessionId] = useState(getInitialSession);
  const [name, setName] = useState("Cliente Demo");
  const [phone, setPhone] = useState("593990000000");
  const [configured, setConfigured] = useState(null);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [paused, setPaused] = useState(false);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/n8n-agents/config`, { withCredentials: true })
      .then((response) => { if (!cancelled) setConfigured(Boolean(response.data?.principal?.configured)); })
      .catch(() => { if (!cancelled) setConfigured(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const resetSession = () => {
    files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
    const created = newSessionId();
    window.sessionStorage.setItem(SESSION_KEY, created);
    setSessionId(created); setMessages([]); setInput(""); setFiles([]); setPaused(false);
  };

  const selectFiles = (selected) => {
    const incoming = Array.from(selected || []);
    if (files.length + incoming.length > MAX_FILES) {
      window.alert(`Puedes adjuntar máximo ${MAX_FILES} archivos por mensaje.`); return;
    }
    const invalid = incoming.find((file) => file.size > MAX_FILE_SIZE);
    if (invalid) { window.alert(`${invalid.name} supera el máximo de 20 MB.`); return; }
    setFiles((current) => [...current, ...incoming.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
    }))]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles((current) => {
      const item = current[index]; if (item?.preview) URL.revokeObjectURL(item.preview);
      return current.filter((_, i) => i !== index);
    });
  };

  const sendMessage = async () => {
    const message = input.trim();
    if (sending || (!message && files.length === 0) || paused) return;
    const outboundFiles = files;
    setMessages((current) => [...current, {
      role: "user", content: message, attachments: outboundFiles.map((item) => ({
        name: item.file.name, size: item.file.size, type: item.file.type, preview: item.preview,
      })),
    }]);
    setInput(""); setFiles([]); setSending(true);
    try {
      const form = new FormData();
      form.append("message", message);
      form.append("session_id", sessionId);
      form.append("phone", phone);
      form.append("name", name);
      outboundFiles.forEach((item) => form.append("files", item.file));
      const response = await axios.post(`${API_URL}/api/n8n-agents/principal/chat-with-files`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const isPaused = Boolean(response.data?.paused);
      setPaused(isPaused);
      const returned = Array.isArray(response.data?.messages)
        ? response.data.messages.filter((item) => typeof item === "string" && item.trim()) : [];
      const reply = String(response.data?.reply || "").trim();
      const replies = returned.length ? returned : (reply ? [reply] : []);
      if (!response.data?.automated_reply_suppressed && replies.length) {
        setMessages((current) => [...current, ...replies.map((content) => ({
          role: "assistant", content, needsHuman: Boolean(response.data?.needs_human), quoteGenerated: Boolean(response.data?.quote_generated),
        }))]);
      }
      if (response.data?.session_id) {
        window.sessionStorage.setItem(SESSION_KEY, response.data.session_id); setSessionId(response.data.session_id);
      }
      setConfigured(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 503) setConfigured(false);
      setMessages((current) => [...current, { role: "assistant", content: detail || "No pude comunicarme con el Agente Principal.", isError: true }]);
    } finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="gimmicks-agent-demo-page">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#4F9A87] text-xs font-semibold uppercase tracking-[0.18em] mb-2"><Bot className="w-4 h-4" /> Sandbox comercial</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Agente Principal · Demo n8n</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl">Prueba texto, imágenes y documentos con el mismo contexto persistente del CRM.</p>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold w-fit ${configured === true ? "border-emerald-200 bg-emerald-50 text-emerald-700" : configured === false ? "border-amber-200 bg-amber-50 text-amber-700" : "border-gray-200 bg-white text-gray-500"}`}>
          {configured === true ? <CheckCircle2 className="w-4 h-4" /> : configured === false ? <CircleAlert className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
          {configured === true ? "n8n configurado" : configured === false ? "Falta configurar webhook n8n" : "Verificando conexión"}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-5">
        <Card className="h-fit border-gray-200 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><UserRound className="w-4 h-4 text-[#63AC9A]" /> Contacto de prueba</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <label className="block"><span className="text-xs font-medium text-gray-600">Nombre</span><input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full h-10 rounded-xl border border-gray-200 px-3 text-sm" /></label>
            <label className="block"><span className="text-xs font-medium text-gray-600">Teléfono Ecuador</span><input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 w-full h-10 rounded-xl border border-gray-200 px-3 text-sm" /></label>
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3"><p className="text-[10px] uppercase font-semibold text-gray-400">Sesión</p><p className="text-[11px] text-gray-500 mt-1 break-all">{sessionId}</p></div>
            <Button variant="outline" onClick={resetSession} className="w-full gap-2 rounded-xl"><RefreshCw className="w-4 h-4" /> Nueva conversación</Button>
            <div className="rounded-xl border border-[#63AC9A]/20 bg-[#63AC9A]/5 p-3 text-xs text-gray-600 leading-relaxed">Adjunta hasta 5 archivos de 20 MB cada uno: imágenes, PDF, Word, Excel, CSV o TXT.</div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm overflow-hidden min-h-[680px] flex flex-col">
          <div className="bg-[#1a1a1d] text-white px-4 py-3 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-[#63AC9A] flex items-center justify-center"><Bot className="w-5 h-5" /></div><div><p className="font-semibold text-sm">Asistente Inteligente de Gimmicks Ecuador</p><p className="text-[11px] text-gray-400">Demo conectada mediante el backend del CRM</p></div></div>
          {paused && <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 font-medium">Agente pausado. Solo puede reactivarse manualmente desde el Inbox del CRM.</div>}
          <div className="flex-1 min-h-0 bg-[#efeae2] px-3 sm:px-5 py-5 overflow-y-auto space-y-3">
            {messages.length === 0 && <div className="text-center text-xs text-gray-500 py-8">Escribe el primer mensaje del cliente o adjunta un archivo para comenzar.</div>}
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] sm:max-w-[75%] rounded-2xl px-3.5 py-2.5 shadow-sm ${message.role === "user" ? "bg-[#d9fdd3] text-gray-800 rounded-br-md" : "bg-white text-gray-800 rounded-bl-md"}`}>
                  {message.content && <p className={`text-sm leading-relaxed whitespace-pre-wrap ${message.isError ? "text-red-600" : ""}`}>{message.content}</p>}
                  {message.attachments?.map((att, i) => <div key={i} className="mt-2 rounded-lg border border-black/10 bg-white/60 overflow-hidden">{att.preview ? <img src={att.preview} alt={att.name} className="max-h-48 w-full object-contain bg-gray-50" /> : <div className="flex items-center gap-2 p-2 text-xs"><FileText className="w-4 h-4" /><span className="truncate">{att.name}</span><span className="text-gray-400">{sizeLabel(att.size)}</span></div>}</div>)}
                  {message.needsHuman && <p className="mt-2 text-[11px] font-semibold text-amber-700">Conversación derivada a atención humana.</p>}
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-white rounded-2xl px-4 py-3 shadow-sm flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-4 h-4 animate-spin text-[#63AC9A]" /> Analizando y respondiendo…</div></div>}
            <div ref={bottomRef} />
          </div>

          <div className="bg-[#f0f2f5] border-t border-gray-200 p-3">
            {files.length > 0 && <div className="flex gap-2 overflow-x-auto pb-2">{files.map((item, index) => <div key={`${item.file.name}-${index}`} className="relative min-w-[150px] max-w-[210px] bg-white border rounded-xl p-2 flex items-center gap-2">{item.preview ? <img src={item.preview} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <FileText className="w-8 h-8 text-gray-400" />}<div className="min-w-0"><p className="text-xs font-medium truncate">{item.file.name}</p><p className="text-[10px] text-gray-400">{sizeLabel(item.file.size)}</p></div><button type="button" onClick={() => removeFile(index)} className="absolute -top-1 -right-1 bg-gray-800 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button></div>)}</div>}
            <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => selectFiles(e.target.files)} />
              <button type="button" disabled={sending || paused} onClick={() => fileRef.current?.click()} className="w-11 h-11 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 disabled:opacity-40" title="Adjuntar archivos"><Paperclip className="w-5 h-5" /></button>
              <textarea value={input} disabled={paused} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} rows={1} placeholder={paused ? "Agente pausado desde el CRM" : "Escribe un mensaje"} className="flex-1 min-h-[44px] max-h-28 resize-none rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#63AC9A] disabled:bg-gray-100" />
              <Button type="submit" size="icon" disabled={sending || paused || (!input.trim() && files.length === 0)} className="h-11 w-11 rounded-full bg-[#63AC9A] hover:bg-[#559c8b] text-white">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
